import React, { useEffect, useState, useContext, useCallback, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes, useLocation, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import useDesktop from "./hooks/useDesktop";
import usePrefersReducedMotion from "./hooks/usePrefersReducedMotion";
import useAnimationOverride from "./hooks/useAnimationOverride";
import useGlobalFetchLoading from "./hooks/useGlobalFetchLoading";
import { LoadingProvider } from "./context/LoadingContext";



import NameGuard from "./components/NameGuard";
// Importe für deine Komponenten
import PageWrapper from "./components/PageWrapper";
// KORREKTUR: Importiere CookieModal, da deine Datei so heißt
import CookieModal from "./components/CookieModal"; // <-- HIER IST DIE KORREKTUR


// Import ZenCursor Customizer 

import ZenCursor from "./kits/DesignKit/ZenCursor.jsx";
import "./kits/DesignKit/zenCursor.css";
import Navbar from "./components/Navbar";
import Preloader from "./components/Preloader";
import GlobalLoadingOverlay from "./components/GlobalLoadingOverlay";
import { CourseProvider } from "./context/CourseContext";
import { NameProvider, NameContext } from "./context/NameContext";
import { ThemeProvider } from "./context/ThemeContext";
import BitterButtonParentSimple from "./components/BitterButtonParentSimple";
import NameForm from "./components/NameForm";
import ScrollToTop from "./components/scrollTo.jsx";
import PersonalizedMarketing from "./components/PersonaliseMarketing";
import SeoHelmet from "./components/SeoHelmet.jsx";
import { AuthProvider } from "./auth/AuthContext";
import {PopularCourseProvider} from "./context/PopularCourseContext.js";
import { scrollHeaderIntoView } from "./components/utils/scrollToHeader";
import { PaperDarkBG } from "./kits/index.js";

const Math = lazy(() => import("./components/math.jsx"));
const ProfessionEditor = lazy(() => import("./components/ProfessionEditor.jsx"));
const NotFound = lazy(() => import("./components/404.jsx"));
const Kompass = lazy(() => import("./components/Kompass"));
const About = lazy(() => import("./components/About"));
const Courses = lazy(() => import("./components/Courses"));
const Course = lazy(() => import("./components/Course"));
const NewCourse = lazy(() => import("./components/NewCourse"));
const EditCourse = lazy(() => import("./components/EditCourse"));
const LoginPage = lazy(() => import("./components/LoginAdmin"));
const Register = lazy(() => import("./components/register"));
const Contact = lazy(() => import("./components/Contact"));
const Login = lazy(() => import("./components/Login"));
const Dashboard = lazy(() => import("./components/Dashboard"));
const AdminResetPassword = lazy(() => import("./components/AdminResetPassword.jsx"));
const ResetPassword = lazy(() => import("./components/ResetPassword.jsx"));
const CourseDetailsAndRegistration = lazy(() => import("./components/CourseDetailsAndRegistration"));
const ZenLab2 = lazy(() => import("./components/ZenLab2"));
const SocialPost = lazy(() => import("./components/SocialPost"));
const Succes = lazy(() => import("./components/Succes"));
const AdminDashboard = lazy(() => import("./components/AdminDashboard.js"));
const ForgotPassword = lazy(() => import("./components/ForgotPassword.js"));
const UserDashboard = lazy(() => import("./components/UserDashboard.jsx"));
const AdminCourseCockpit = lazy(() => import("./components/AdminCourseCockpit.jsx"));
const PatternKit = lazy(() => import("./kits/DesignKit/PatternKit"));
const LessonTabs = lazy(() => import("./courses/LessonTabs.jsx"));
const LogbookEntry = lazy(() => import("./components/LogbookEntry.jsx"));
const Blog = lazy(() => import("./components/Blog.jsx"));
const Post = lazy(() => import("./components/Post.jsx"));
const BlogEditor = lazy(() => import("./components/BlogEditor.jsx"));
const ZenPost = lazy(() => import("./components/ZenPost.jsx"));
const CasePost = lazy(() => import("./components/CasePost.jsx"));
const LinkedinBlogEditor = lazy(() => import("./components/LinkedinBlogEditor.jsx"));
const SmartBlogEditor = lazy(() => import("./components/SmartBlogEditor.jsx"));
const SmartZenEditor = lazy(() => import("./components/SmartZenEditor.jsx"));
const Datenschutz = lazy(() => import("./components/Datenschutz.jsx"));
const Impressum = lazy(() => import("./components/Impressum.jsx"));
const SkillManager = lazy(() => import("./components/SkillManager.jsx"));
const ProjectManager = lazy(() => import("./components/ProjectManager.jsx"));
const BuildCustomCourse = lazy(() => import("./components/BuildCustomCourse.jsx"));
const UserCustomDashboard = lazy(() => import("./components/UserCustomDashboard.jsx"));
const CourseCockpitUser = lazy(() => import("./components/CourseCockpitUser.jsx"));
const BuilderPage = lazy(() => import("./components/BuilderPage.jsx"));
const ZenEditorLandingPage = lazy(() => import("./components/ZenEditorLandingPage.jsx"));
const ZenPostStudioLandingPage = lazy(() => import("./components/ZenPostStudioLandingPage.jsx"));
const SearchResults = lazy(() => import("./components/SearhResults"));
const CourseNew = lazy(() => import("./components/CourseNew"));
const CourseCopy = lazy(() => import("./components/CourseCopy"));
const ProjectDetail = lazy(() => import("./routes/ProjectDetail"));
const Callback = lazy(() => import("./components/Callback.jsx"));
const SmartCaseEditor = lazy(() => import("./components/SmartCaseEditor.jsx"));
const Footer = lazy(() => import("./components/Footer"));
const SectionD = lazy(() => import("./components/SectionD.jsx"));
const SectionZen = lazy(() => import("./components/SectionZen.jsx"));
const SectionBlog = lazy(() => import("./components/SectionBlog.jsx"));
const BlogHeroList = lazy(() => import("./components/BlogHeroList.jsx"));
const ZenDevHeroList = lazy(() => import("./components/ZenDevHeroList.jsx"));
const CourseHeroList = lazy(() => import("./components/CourseHeroList"));
const ZenPostStudioLandingPageHero = lazy(() => import("./components/ZenPostStudioLandingPageHero.jsx"));
const ZenOrbitLandingPageHero = lazy(() => import("./components/ZenOrbitLandingPageHero.jsx"));

// OrbitCustomizer wurde zu zenorbit.denisbitter.de ausgelagert
const ExternalRedirect = ({ to }) => {
  useEffect(() => { window.location.replace(to); }, [to]);
  return null;
};

const DeferUntilVisible = ({ children, rootMargin = "300px 0px", minHeight = 120 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (isVisible) return;
    if (typeof window === "undefined") return;
    if (!containerRef.current) return;

    if (!("IntersectionObserver" in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold: 0.01 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return <div ref={containerRef} style={{ minHeight }}>{isVisible ? children : null}</div>;
};

// Diese Komponente kapselt die Layout- und Navigationslogik
function AppContent() {
  const [isReady, setIsReady] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("appPreloaderShown") === "1";
  }); // Für den globalen Preloader (nur einmal pro Session)
  useGlobalFetchLoading();
  const location = useLocation();
  const [navbarHeight, setNavbarHeight] = useState(0); // Zustand für die dynamische Navbar-Höhe
  const { isNameEntered = false } = useContext(NameContext); // NameContext, um isNameEntered zu bekommen

  // NEUER STATE FÜR COOKIE MODAL SICHTBARKEIT
  const [showCookieModal, setShowCookieModal] = useState(false); // <-- Hier 'showCookieModal' verwenden
  // NEUER STATE FÜR DEN INITIALEN COOKIE CONSENT ZUSTAND (wird geladen und dann an Modal übergeben)
  const [initialCookieConsent, setInitialCookieConsent] = useState(null);
  const cookieModalRef = useRef(null); // Ref für das Cookie-Modal


  // USEEFFECT für Blog zum scrollen ohne Namem 

    // 🔐 Definiere Ausnahmen für isNameEntered
  const nameBypassRoutes = [
    "/blog",
    "/post/",
    "/zen/",
    "/case/",
    "/datenschutz",
    "/impressum",
    "/zenlab2",
    "/zenlab",
    "/contact"
  ];
  const isBypassed = nameBypassRoutes.some((prefix) =>
    location.pathname.startsWith(prefix)
  );


  // Browser Scroll-Restore deaktivieren (verhindert Sprünge)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
      return () => {
        window.history.scrollRestoration = "auto";
      };
    }
  }, []);






/* // neuer Preloader ohne Time 

useEffect(() => {
  const initApp = async () => {
    // Hier: echtes Laden/Authentifizieren, z.B. Cookies, API, etc.
    // await irgendwasEchtes();

    // Cookie-Zustimmung prüfen
    const stored = localStorage.getItem("cookieConsentV2");
    if (!stored) {
      setShowCookieModal(true);
      setInitialCookieConsent({
        essential: true,
        statistics: false,
        media: false,
      });
    } else {
      setInitialCookieConsent(JSON.parse(stored));
    }
    setIsReady(true); // Sobald alles geladen/gecheckt
  };
  initApp();
}, []);
 */


// alter TimeOut Preoloader 
  // Globaler Preloader für die App-Initialisierung UND Cookie-Check
  useEffect(() => {
    const initApp = async () => {
      const alreadyShown =
        typeof window !== "undefined" && sessionStorage.getItem("appPreloaderShown") === "1";

      if (!alreadyShown) {
        await new Promise(resolve => setTimeout(resolve, 1500)); // Globale Verzögerung
        if (typeof window !== "undefined") {
          sessionStorage.setItem("appPreloaderShown", "1");
        }
      }
      setIsReady(true);

      // Überprüfe Cookie-Zustimmung beim App-Start
      const stored = localStorage.getItem("cookieConsentV2");
      if (!stored) {
        // Wenn keine Zustimmung gefunden, Modal anzeigen
        setShowCookieModal(true); // <-- Hier 'setShowCookieModal' verwenden
        // Initialisiere den Consent-State mit Standardwerten, da noch nichts gespeichert ist
        setInitialCookieConsent({
          essential: true,
          statistics: false,
          media: false,
        });
      } else {
        // Wenn Zustimmung gefunden, lade sie und speichere sie, aber Modal nicht anzeigen
        setInitialCookieConsent(JSON.parse(stored));
      }
    };
    initApp();
  }, []); // Leeres Array, damit dies  

  // Callback-Funktion, um die Navbar-Höhe von der Navbar-Komponente zu empfangen
  const updateNavbarHeight = useCallback((newHeight) => {
    setNavbarHeight(newHeight);
  }, []);

  const isDesktop = useDesktop();
  const prefersReduced = usePrefersReducedMotion();
  const animOverride = useAnimationOverride();
  const shouldAnimate = animOverride ?? (isDesktop && !prefersReduced);
  const [homeHeroDone, setHomeHeroDone] = useState(!shouldAnimate);
  const [homeAccordionOpen, setHomeAccordionOpen] = useState(null);
  const zenListRef = useRef(null);
  const serviceHeaderRef = useRef(null);
  const techHeaderRef = useRef(null);
  const zenHeaderRef = useRef(null);
  const blogHeaderRef = useRef(null);
  const courseHeaderRef = useRef(null);
  const blogListRef = useRef(null);
  const courseListRef = useRef(null);

  useEffect(() => {
    if (location.pathname === "/" && shouldAnimate) setHomeHeroDone(false);
  }, [location.pathname, shouldAnimate]);

  useEffect(() => {
    if (!shouldAnimate) setHomeHeroDone(true);
  }, [shouldAnimate]);

  useEffect(() => {
    if (location.pathname !== "/") {
      setHomeAccordionOpen(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/" || !homeAccordionOpen) return;
    const sectionRefs = {
      services: serviceHeaderRef,
      tech: techHeaderRef,
      zen: zenHeaderRef,
      blog: blogHeaderRef,
      courses: courseHeaderRef,
    };
    const target = sectionRefs[homeAccordionOpen]?.current;
    if (!target) return;

    // Wait for the 700ms CSS close-transition of the previous section to finish
    const timer = setTimeout(() => {
      scrollHeaderIntoView(target, "smooth", 8);
    }, 720);
    return () => clearTimeout(timer);
  }, [homeAccordionOpen, location.pathname]);

  useEffect(() => {
    document.documentElement.style.setProperty("--navbar-height", `${navbarHeight}px`);
    return () => {
      document.documentElement.style.removeProperty("--navbar-height");
    };
  }, [navbarHeight]);

  // Funktion zum Schließen des Cookie-Modals
  const handleCloseCookieModal = useCallback(() => { // <-- Hier 'handleCloseCookieModal'
    setShowCookieModal(false); // <-- Hier 'setShowCookieModal' verwenden
  }, []);

  const toggleHomeAccordion = useCallback((section) => {
    setHomeAccordionOpen((current) => (current === section ? null : section));
  }, []);




  // Der globale Preloader für die gesamte App
  if (!isReady) {
    return <Preloader isReady={isReady} />;
  }

  return (
    <div className={`App ${!isNameEntered && !isBypassed ? "overflow-hidden h-screen" : ""}`}>
{isNameEntered && <Navbar updateNavbarHeight={updateNavbarHeight} />}


       {!isNameEntered && !isBypassed && <NameForm />}

      <div
        className="main-content relative"
        style={{ paddingTop: `${navbarHeight}px`, "--navbar-height": `${navbarHeight}px` }}
      >
        <GlobalLoadingOverlay />

  <ZenCursor />
        <ScrollToTop />
        {/* HIER WIRD DAS COOKIE-MODAL BEDINGT GERENDERT */}
        {showCookieModal && ( // <-- Hier 'showCookieModal' verwenden
          <CookieModal // <-- HIER CookieModal verwenden
            ref={cookieModalRef} // Ref für Klick außerhalb
            initialConsent={initialCookieConsent} // Initialen Zustand übergeben
            onClose={handleCloseCookieModal} // <-- Hier 'handleCloseCookieModal' verwenden
          />
        )}
        {/* Die alte <CookieModal /> Zeile sollte hier entfernt werden,
            da wir jetzt die neue, gesteuerte CookieModal Komponente verwenden */}
        {/* <CookieModal />  <-- DIESE ZEILE MUSS ENTFERNT WERDEN */}

       {isNameEntered && <BitterButtonParentSimple />}
       <AnimatePresence mode="wait">
  <Suspense fallback={null}>
  <Routes location={location} key={location.pathname}>
    {/* Hauptseite bleibt ohne Guard */}
    <Route path="/" element={
      <>
<SeoHelmet
  title="denis bitter — software fullstack architect"
  description="design + code. entwickler + dozent. react · angular · flutter · system architecture."
  url="https://www.denisbitter.de/"
  image="https://www.denisbitter.de/images/hero-og.png"
/>

        <PageWrapper>
          <PersonalizedMarketing
            id="personalized-section"
            onHeroDone={() => setHomeHeroDone(true)}
            homeAccordionOpen={homeAccordionOpen}
            onAccordionToggle={toggleHomeAccordion}
            serviceHeaderRef={serviceHeaderRef}
            techHeaderRef={techHeaderRef}
          />
          <main
            className={`${homeHeroDone ? "opacity-100" : "opacity-0 pointer-events-none"} transition-opacity duration-900 ease-out`}
            aria-hidden={!homeHeroDone}
          >
            <div className="zenlist-area">
              <div className="mt-0">
                <SectionZen
                  onToggle={() => toggleHomeAccordion("zen")}
                  isOpen={homeAccordionOpen === "zen"}
                  headerRef={zenHeaderRef}
                  autoScrollOnOpen={false}
                />
              </div>
              <div
                ref={zenListRef}
                className={`mt-0 overflow-hidden transform-gpu transition-[max-height,opacity,transform] duration-700 ease-out will-change-[max-height,opacity,transform] ${
                  homeAccordionOpen === "zen"
                    ? "max-h-[2000px] opacity-100 translate-y-0"
                    : "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
                }`}
                aria-hidden={homeAccordionOpen !== "zen"}
              >
                <PaperDarkBG>
                  <ZenDevHeroList />
                </PaperDarkBG>
              </div>
            </div>
            <SectionBlog
              onToggle={() => toggleHomeAccordion("blog")}
              isOpen={homeAccordionOpen === "blog"}
              headerRef={blogHeaderRef}
              autoScrollOnOpen={false}
            />
            
            <div
              ref={blogListRef}
              className={`overflow-hidden transform-gpu transition-[max-height,opacity,transform] duration-700 ease-out will-change-[max-height,opacity,transform] ${
                homeAccordionOpen === "blog"
                  ? "max-h-[2400px] opacity-100 translate-y-0"
                  : "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
              }`}
              aria-hidden={homeAccordionOpen !== "blog"}
            >
              <PaperDarkBG>
            <BlogHeroList />
            </PaperDarkBG>
            </div>

            <SectionD
              onToggle={() => toggleHomeAccordion("courses")}
              isOpen={homeAccordionOpen === "courses"}
              headerRef={courseHeaderRef}
              autoScrollOnOpen={false}
            />
            <div
              ref={courseListRef}
              className={`overflow-hidden transform-gpu transition-[max-height,opacity,transform] duration-700 ease-out will-change-[max-height,opacity,transform] ${
                homeAccordionOpen === "courses"
                  ? "max-h-[2400px] opacity-100 translate-y-0"
                  : "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
              }`}
              aria-hidden={homeAccordionOpen !== "courses"}
            >
              <PaperDarkBG>
                <CourseHeroList />
              </PaperDarkBG>
            </div>
                   
   <DeferUntilVisible minHeight={220}>
     <ZenPostStudioLandingPageHero />
   </DeferUntilVisible>
   <DeferUntilVisible minHeight={220}>
     <ZenOrbitLandingPageHero />
   </DeferUntilVisible>
          </main>
        </PageWrapper>
      </>
    } />

    {/* ALLE WEITEREN Routen um NameGuard wickeln, ausgenommen explizite Ausnahmen */}
    <Route element={<NameGuard />}>  {/* Alles darunter wird geschützt */}
     
    </Route>

    {/* AUSSNAHMEN vom Guard: öffentlich */}
    <Route path="/zeneditor" element={<PageWrapper><ZenEditorLandingPage /></PageWrapper>} />
    <Route path="/zenpost-studio" element={<PageWrapper><ZenPostStudioLandingPage /></PageWrapper>} />
    <Route path="/login" element={<PageWrapper><Login /></PageWrapper>} />
    <Route path="/loginadmin" element={<PageWrapper><LoginPage /></PageWrapper>} />
    <Route path="/blog" element={<PageWrapper><Blog /></PageWrapper>} />
    <Route path="/post/:slug" element={<PageWrapper><Post /></PageWrapper>} />
    <Route path="/zen/:slug" element={<PageWrapper><ZenPost /></PageWrapper>} />
      <Route path="/casestudy/:slug" element={<PageWrapper><CasePost /></PageWrapper>} />
    <Route path="/datenschutz" element={<PageWrapper><Datenschutz /></PageWrapper>} />
    <Route path="/impressum" element={<PageWrapper><Impressum /></PageWrapper>} />
    <Route path="/zenlab" element={<Navigate to="/zenlab2" replace />} />
    <Route path="/zenlab2" element={<PageWrapper><ZenLab2 /></PageWrapper>} />
    <Route path="/lernstudio" element={<Navigate to="/zenlab2" replace />} />
       <Route path="/admindashboard" element={<PageWrapper><AdminDashboard /></PageWrapper>} />
          <Route path="/about" element={<PageWrapper><About /></PageWrapper>} />
      <Route path="/course-edit/edit:slug" element={<PageWrapper><EditCourse /></PageWrapper>} />
      <Route path="/course-edit/new" element={<EditCourse />} />
      <Route path="/course/new" element={<EditCourse />} />
<Route path="/course/edit/:slug" element={<EditCourse />} />




    <Route path="*" element={<PageWrapper><NotFound /></PageWrapper>} />
     <Route path="/logbuch" element={<PageWrapper><LogbookEntry /></PageWrapper>} />
     <Route path="/orbit" element={<ExternalRedirect to="https://zenorbit.denisbitter.de" />} />

      <Route path="/math" element={<PageWrapper><Math /></PageWrapper>} />

      <Route path="/userdashboard" element={<PageWrapper><UserDashboard /></PageWrapper>} />
      <Route path="/courses" element={<PageWrapper><Courses /></PageWrapper>} />
      <Route path="/course/:slug" element={<PageWrapper><Course /></PageWrapper>} />
      <Route path="/new-course" element={<PageWrapper><NewCourse /></PageWrapper>} />


    <Route path="/callback" element={<PageWrapper><Callback /></PageWrapper>} />

      <Route path="/register" element={<PageWrapper><Register /></PageWrapper>} />
      <Route path="/contact" element={<PageWrapper><Contact /></PageWrapper>} /> 
      <Route path="/anmeldung/:slug" element={<PageWrapper><CourseDetailsAndRegistration /></PageWrapper>} />
      <Route path="/dashboard" element={<PageWrapper><Dashboard /></PageWrapper>} />
      <Route path="/reset-password" element={<PageWrapper><ResetPassword /></PageWrapper>} />
      <Route path="/adminresetpassword" element={<PageWrapper><AdminResetPassword /></PageWrapper>} />
      <Route path="/search" element={<PageWrapper><SearchResults /></PageWrapper>} />
      <Route path="/socialpost" element={<PageWrapper><SocialPost /></PageWrapper>} />
      <Route path="/success" element={<PageWrapper><Succes /></PageWrapper>} />
      <Route path="/kompass" element={<PageWrapper><Kompass /></PageWrapper>} />
      <Route path="/patternkit" element={<PageWrapper><PatternKit /></PageWrapper>} />
      <Route path="/lesson-tabs" element={<PageWrapper><LessonTabs /></PageWrapper>} />
      <Route path="/coursecockpit/:courseId" element={<PageWrapper><CourseCockpitUser /></PageWrapper>} />
      <Route path="/blogeditor" element={<PageWrapper><BlogEditor /></PageWrapper>} />
      <Route path="/linkedinblogeditor" element={<PageWrapper><LinkedinBlogEditor /></PageWrapper>} />
      <Route path="/buildpage" element={<PageWrapper><BuilderPage /></PageWrapper>} />
      <Route path="/smarteditor" element={<PageWrapper><SmartBlogEditor /></PageWrapper>} />
      <Route path="/smartcaseeditor" element={<PageWrapper><SmartCaseEditor /></PageWrapper>} />
      <Route path="/professioneditor" element={<PageWrapper><ProfessionEditor /></PageWrapper>} />
      <Route path="/smartzeneditor" element={<PageWrapper><SmartZenEditor /></PageWrapper>} />
      <Route path="/skillmanager" element={<PageWrapper><SkillManager /></PageWrapper>} />
      <Route path="/prmanager" element={<PageWrapper><ProjectManager /></PageWrapper>} />
      <Route path="/bausteinkurs" element={<PageWrapper><BuildCustomCourse /></PageWrapper>} />
      <Route path="/dashboard-custom" element={<PageWrapper><UserCustomDashboard /></PageWrapper>} />
      <Route path="/coursematerial" element={<PageWrapper><AdminCourseCockpit /></PageWrapper>} />
      <Route path="/forgot-password" element={<PageWrapper><ForgotPassword /></PageWrapper>} />
      <Route path="/project/:slug" element={<PageWrapper><ProjectDetail /></PageWrapper>} />
      <Route path="/course-new" element={<PageWrapper><CourseNew /></PageWrapper>} />
      <Route path="/course-copy/:id" element={<PageWrapper><CourseCopy /></PageWrapper>} />
  </Routes>
  </Suspense>
</AnimatePresence>

      </div>
      {/* Footer erhält den Callback, um das Cookie-Modal zu öffnen */}
      <Suspense fallback={null}>
        <Footer onShowCookieSettings={() => setShowCookieModal(true)} /> {/* <-- Hier 'setShowCookieModal' */}
      </Suspense>
    </div>
  );
}

// Dies ist deine MainApp, die die Provider und den BrowserRouter kapselt.
const MainApp = () => (
  <AuthProvider>
    <CourseProvider>
      <PopularCourseProvider>
        <BrowserRouter>
          <LoadingProvider>
            <NameProvider>
              <ThemeProvider>
                <AppContent />
              </ThemeProvider>
            </NameProvider>
          </LoadingProvider>
        </BrowserRouter>
      </PopularCourseProvider>
    </CourseProvider>
  </AuthProvider>
);

export default MainApp;
